import { useMemo } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, Flex, List, SimpleGrid, Stack, Title } from "@mantine/core";
import {
  IconArrowRight,
  IconBolt,
  IconBuildingFactory2,
  IconHeartRateMonitor,
  IconLeaf,
  IconPlaneTilt,
  IconSparkles,
  IconTractor,
  IconTopologyStar3,
} from "@tabler/icons-react";
import { HOME_SECTOR_CARDS, type SectorCard } from "./sectorCatalog";

const sectorIconBySlug = {
  "energy-utilities": IconBolt,
  "industrial-manufacturing": IconBuildingFactory2,
  "mobility-aerospace-defence": IconPlaneTilt,
  "infrastructure-built-environment": IconTopologyStar3,
  "technology-electronics": IconSparkles,
  "environment-sustainability": IconLeaf,
  "agriculture-food-systems": IconTractor,
  "healthcare-life-sciences": IconHeartRateMonitor,
} as const;

export type SectorCardsGridProps = {
  /** Full grid on home/discover vs single column for discover sidebar filter. */
  variant?: "grid" | "sidebar";
  /** Called when user picks a sector (before navigation) — e.g. Discover moves cards to a left filter column. */
  onFilterEngaged?: () => void;
  /** When provided, sector/list clicks call this instead of navigating with keyword query. */
  onSectorSelect?: (sector: SectorCard) => void;
  /** Home: deep-link to `/sector/:slug`. Discover: stay on `/discover` with keyword query. */
  viewCompaniesNavMode?: "sector-route" | "discover-keyword";
};

function navigateToDiscoverKeyword(navigate: ReturnType<typeof useNavigate>, keyword: string) {
  navigate({
    pathname: "/discover",
    search: `?keyword=${encodeURIComponent(keyword)}`,
  });
}

function viewCompanies(
  navigate: ReturnType<typeof useNavigate>,
  sector: SectorCard,
  mode: SectorCardsGridProps["viewCompaniesNavMode"],
) {
  if (mode === "discover-keyword") {
    navigateToDiscoverKeyword(navigate, sector.searchKeyword);
    return;
  }
  navigate(`/sector/${sector.slug}`);
}

export function SectorCardsGrid({
  variant = "grid",
  onFilterEngaged,
  onSectorSelect,
  viewCompaniesNavMode = "sector-route",
}: SectorCardsGridProps) {
  const navigate = useNavigate();
  const sortedSectorCards = useMemo(
    () => [...HOME_SECTOR_CARDS].sort((a, b) => b.subcategories.length - a.subcategories.length),
    [],
  );

  const engage = () => {
    onFilterEngaged?.();
  };

  const stopCardClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleSectorSelect = (sector: SectorCard) => {
    engage();
    if (onSectorSelect) {
      onSectorSelect(sector);
      return;
    }
    viewCompanies(navigate, sector, viewCompaniesNavMode);
  };

  const gridCols = variant === "sidebar" ? 1 : { base: 1, sm: 2, lg: 4 };
  const cardPadding = variant === "sidebar" ? "sm" : "md";
  const titleSize = variant === "sidebar" ? 15 : 18;
  const iconSize = variant === "sidebar" ? 18 : 20;

  return (
    <SimpleGrid cols={gridCols} spacing={variant === "sidebar" ? "sm" : "md"}>
      {sortedSectorCards.map((sector) => {
        const SectorIcon = sectorIconBySlug[sector.slug as keyof typeof sectorIconBySlug] ?? IconSparkles;
        return (
          <Card
            key={sector.slug}
            withBorder
            radius="md"
            shadow="xs"
            p={cardPadding}
            style={{ cursor: "pointer" }}
            onClick={() => handleSectorSelect(sector)}
          >
            <Stack gap={variant === "sidebar" ? "xs" : "sm"} h="100%">
              <Flex align="center" gap={8}>
                <SectorIcon size={iconSize} color="#04305c" />
                <Title order={4} fz={titleSize} lineClamp={variant === "sidebar" ? 2 : undefined}>
                  {sector.title}
                </Title>
              </Flex>
              <List size={variant === "sidebar" ? "xs" : "sm"} spacing={6}>
                {sector.subcategories.map((item) => (
                  <List.Item
                    key={item}
                    style={{ cursor: "pointer", color: "#04305c", textDecoration: "underline" }}
                    onClick={(e) => {
                      stopCardClick(e);
                      engage();
                      if (onSectorSelect) {
                        onSectorSelect(sector);
                        return;
                      }
                      navigateToDiscoverKeyword(navigate, item);
                    }}
                  >
                    {item}
                  </List.Item>
                ))}
              </List>
              <Box mt="auto" onClick={stopCardClick}>
                <Button
                  variant="light"
                  color="blue"
                  radius="md"
                  fullWidth
                  size={variant === "sidebar" ? "xs" : "sm"}
                  onClick={(e) => {
                    stopCardClick(e);
                    handleSectorSelect(sector);
                  }}
                  rightSection={<IconArrowRight size={14} />}
                >
                  View Companies
                </Button>
              </Box>
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}
